import { useEffect, useCallback } from 'react'
import zionDebug from '@fs/zion-debug'
import { getSession } from '@fs/zion-session'
import { useDarkModeContext } from '../providers/DarkModeProvider'

const debug = zionDebug('together:useTWAVisibilityHandler')
const MAX_Z_INDEX = '2147483647'
const OVERLAY_ID = '__twa_visibility_overlay'

export default function useTWAVisibilityHandler({ isTWAMode }) {
  const { isDarkMode } = useDarkModeContext()

  const removeOverlay = useCallback(() => {
    const overlay = document.getElementById(OVERLAY_ID)
    if (overlay) {
      overlay.remove()
    }
  }, [])

  const checkSessionAndReload = useCallback(async () => {
    try {
      const sessionInfo = await getSession(true)
      const hasValidSession = sessionInfo?.users?.length > 0 && sessionInfo.users[0]?.id

      if (!hasValidSession) {
        // If session is invalid, reload the app to force a new login
        window.location.reload()
      } else {
        // Remove overlay if session is valid
        removeOverlay()
      }
    } catch (error) {
      debug('Error checking session:', error)
      // On error, reload to be safe
      window.location.reload()
    }
  }, [removeOverlay])

  const createOverlay = useCallback(() => {
    const bg = isDarkMode ? '#000000' : '#FFFFFF'
    let overlay = document.getElementById(OVERLAY_ID)

    if (!overlay) {
      overlay = document.createElement('div')
      overlay.id = OVERLAY_ID
      overlay.setAttribute('aria-hidden', 'true')
      overlay.style.position = 'fixed'
      overlay.style.inset = '0'
      overlay.style.backgroundColor = bg
      overlay.style.zIndex = MAX_Z_INDEX
      overlay.style.pointerEvents = 'none'
      document.body?.appendChild(overlay)
    }
  }, [isDarkMode])

  useEffect(() => {
    const handleVisibility = async () => {
      if (!isTWAMode) {
        return
      }

      // Check if we should reload after sign-in
      let shouldReloadAfterSignIn = false
      try {
        shouldReloadAfterSignIn = !!localStorage.getItem('shouldReloadAfterSignIn')
        // Remove the flag after checking so we only reload once
        if (shouldReloadAfterSignIn) {
          localStorage.removeItem('shouldReloadAfterSignIn')
        }
      } catch (error) {
        debug('useTWAVisibilityHandler: unable to read reload flag from localStorage', error)
      }

      if (document.visibilityState === 'visible') {
        // When the app becomes visible, either reload immediately or check session
        if (shouldReloadAfterSignIn) {
          // This would have been set on the signintwa page
          window.location.reload()
        } else {
          // Check if session is still valid when app becomes visible
          await checkSessionAndReload()
        }
        // Remove overlay if session is valid
        removeOverlay()
      } else {
        // When the app becomes hidden, show overlay
        createOverlay()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [isTWAMode, checkSessionAndReload, createOverlay, removeOverlay])
}
